import React, { useState, Children } from 'react';
import { BookIcon, ClockIcon, DollarSignIcon, UserIcon, FilterIcon, SearchIcon } from 'lucide-react';
import BackButton from '../../components/UI/BackButton';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
const opportunities = [{
  id: 1,
  title: 'Fantasy Series Interior Design',
  description: 'Looking for an experienced typesetter for a 5-book epic fantasy series. Complex formatting with maps, illustrations, and special chapter designs needed.',
  projectType: 'fiction',
  format: ['print', 'ebook'],
  budget: 'premium',
  timeline: '3 months',
  requirements: ['Experience with fantasy books', 'Illustration integration', 'Custom chapter designs', 'Map formatting'],
  status: 'open',
  applicants: 12,
  postedDate: '2023-10-01',
  deadline: '2023-10-15'
}, {
  id: 2,
  title: 'Technical Manual Formatting',
  description: 'Need a formatter for a technical programming manual. Includes code blocks, diagrams, and complex tables.',
  projectType: 'technical',
  format: ['print', 'pdf'],
  budget: 'standard',
  timeline: '6 weeks',
  requirements: ['Technical documentation experience', 'Code formatting expertise', 'Table and diagram layout'],
  status: 'open',
  applicants: 8,
  postedDate: '2023-10-02',
  deadline: '2023-10-20'
}, {
  id: 3,
  title: 'Poetry Collection Typesetting',
  description: 'Seeking a typesetter for a collection of modern poetry. Requires careful attention to spacing and visual presentation.',
  projectType: 'poetry',
  format: ['print', 'ebook'],
  budget: 'premium',
  timeline: '4 weeks',
  requirements: ['Poetry formatting experience', 'Typography expertise', 'Creative layout skills'],
  status: 'open',
  applicants: 5,
  postedDate: '2023-10-03',
  deadline: '2023-10-25'
}];
const projectTypes = [{
  id: 'fiction',
  name: 'Fiction'
}, {
  id: 'nonfiction',
  name: 'Non-Fiction'
}, {
  id: 'technical',
  name: 'Technical'
}, {
  id: 'poetry',
  name: 'Poetry'
}, {
  id: 'childrens',
  name: "Children's"
}];
const formats = [{
  id: 'print',
  name: 'Print'
}, {
  id: 'ebook',
  name: 'eBook'
}, {
  id: 'pdf',
  name: 'PDF'
}, {
  id: 'fixed',
  name: 'Fixed Layout'
}];
const budgetRanges = [{
  id: 'economy',
  name: 'Economy ($2-3/page)'
}, {
  id: 'standard',
  name: 'Standard ($3-4/page)'
}, {
  id: 'premium',
  name: 'Premium ($4+/page)'
}];
const ProductionOpportunities = () => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedFormats, setSelectedFormats] = useState<string[]>([]);
  const [selectedBudget, setSelectedBudget] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const toggle = (val: string, arr: string[], set: React.Dispatch<React.SetStateAction<string[]>>) => set(arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val]);
  const filtered = opportunities.filter(o => {
    const matchesSearch = o.title.toLowerCase().includes(searchQuery.toLowerCase()) || o.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = selectedTypes.length === 0 || selectedTypes.includes(o.projectType);
    const matchesFormat = selectedFormats.length === 0 || o.format.some(f => selectedFormats.includes(f));
    const matchesBudget = selectedBudget.length === 0 || selectedBudget.includes(o.budget);
    return matchesSearch && matchesType && matchesFormat && matchesBudget;
  });
  const checkboxClass = `h-4 w-4 rounded border-gray-300 ${rt.checkbox}`;
  return <div className={`min-h-screen ${dk.page}`} data-id="element-13369" data-mp="3b8tdit34p6lv">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-13370" data-mp="3bc8u60cganpe">
        <div className="mb-8" data-id="element-13371" data-mp="3g7ee4tmalsau">
          <div className="mb-4" data-id="element-13372" data-mp="3gasjmaq96hmi">
            <BackButton data-id="element-13373" data-mp="3l7iu8ijagl0j" />
          </div>
          <h1 className={`text-3xl font-bold ${dk.textHeading}`} data-id="element-13374" data-mp="3l8tzynwao2ua">
            Typesetting & Formatting Opportunities
          </h1>
          <p className={`mt-2 text-lg ${dk.textSecondary}`} data-id="element-13375" data-mp="2sjdd05rg1e5g">
            Find projects that match your expertise and interests
          </p>
        </div>

        {/* Search and Filter Panel */}
        <div className={`rounded-lg shadow-md p-6 mb-8 border ${dk.card}`} data-id="element-13376" data-mp="31k344w5regcu">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4" data-id="element-13377" data-mp="31irxmokxiui1">
            <div className="relative flex-1" data-id="element-13378" data-mp="36c2zebavwl33">
              <SearchIcon className={`h-5 w-5 absolute left-3 top-1/2 -translate-y-1/2 ${dk.textFaint}`} data-id="element-13379" data-mp="29tf6hu3ant3x" />
              <input type="text" placeholder="Search opportunities..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${rt.inputFocus} ${dk.inputOnCard}`} data-id="element-13380" data-mp="2dsxnwfvwpm20" />
            </div>
            <button onClick={() => setShowFilters(!showFilters)} className={`flex items-center gap-2 text-sm font-medium ${rt.accentLink}`} data-id="element-13381" data-mp="1sbzwrnomqwc4">
              <FilterIcon className="h-4 w-4" data-id="element-13382" data-mp="1ikvgmkn7x5fp" />
              Filters {showFilters ? '(Hide)' : '(Show)'}
            </button>
          </div>

          {showFilters && <div className={`border-t pt-4 ${dk.border}`} data-id="element-13383" data-mp="1djxwi2ine1bk">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-13384" data-mp="19m06ixh1zb74">
                <div data-id="element-13385" data-mp="14m5l39g1rdtg">
                  <h3 className={`font-medium mb-3 ${dk.textPrimary}`} data-id="element-13386" data-mp="14kax1m9hb5l5">
                    Project Type
                  </h3>
                  <div className="space-y-2" data-id="element-13387" data-mp="zom4i2vvmoyc">
                    {projectTypes.map(t => <label key={t.id} className="flex items-center gap-2 cursor-pointer" data-id="element-13388" data-mp="pxm11uefraji">
                        <input type="checkbox" checked={selectedTypes.includes(t.id)} onChange={() => toggle(t.id, selectedTypes, setSelectedTypes)} className={checkboxClass} data-id="element-13389" data-mp="g1snq8sw7d0o" />
                        <span className={`text-sm ${dk.textSecondary}`} data-id="element-13390" data-mp="yzlfcey2g0ir">
                          {t.name}
                        </span>
                      </label>)}
                  </div>
                </div>
                <div data-id="element-13391" data-mp="1rn7vemohcxmi">
                  <h3 className={`font-medium mb-3 ${dk.textPrimary}`} data-id="element-13392" data-mp="1rmprr4zhnh4k">
                    Format
                  </h3>
                  <div className="space-y-2" data-id="element-13393" data-mp="1wh2cdicpayhw">
                    {formats.map(f => <label key={f.id} className="flex items-center gap-2 cursor-pointer" data-id="element-13394" data-mp="3h3jtco0dc0tv">
                        <input type="checkbox" checked={selectedFormats.includes(f.id)} onChange={() => toggle(f.id, selectedFormats, setSelectedFormats)} className={checkboxClass} data-id="element-13395" data-mp="3qu670coiyh9x" />
                        <span className={`text-sm ${dk.textSecondary}`} data-id="element-13396" data-mp="376yqo9btn1t7">
                          {f.name}
                        </span>
                      </label>)}
                  </div>
                </div>
                <div data-id="element-13397" data-mp="mfybk6gvsqe5">
                  <h3 className={`font-medium mb-3 ${dk.textPrimary}`} data-id="element-13398" data-mp="mf6cglo2saj4">
                    Budget Range
                  </h3>
                  <div className="space-y-2" data-id="element-13399" data-mp="ihqutbmf3p2y">
                    {budgetRanges.map(b => <label key={b.id} className="flex items-center gap-2 cursor-pointer" data-id="element-13400" data-mp="2bwaaob4i9zn3">
                        <input type="checkbox" checked={selectedBudget.includes(b.id)} onChange={() => toggle(b.id, selectedBudget, setSelectedBudget)} className={checkboxClass} data-id="element-13401" data-mp="2luq2kgbcxcxn" />
                        <span className={`text-sm ${dk.textSecondary}`} data-id="element-13402" data-mp="34m95yo9amt07">
                          {b.name}
                        </span>
                      </label>)}
                  </div>
                </div>
              </div>
            </div>}
        </div>

        {/* Opportunities */}
        <div className="space-y-6" data-id="element-13403" data-mp="1f8ni4oajz1ex">
          {filtered.map(o => <div key={o.id} className={`rounded-lg shadow-md p-6 border ${dk.card}`} data-id="element-13404" data-mp="3sinmr56byzp3">
              <div className="flex flex-col md:flex-row md:items-center justify-between mb-4" data-id="element-13405" data-mp="3nqm14sn9vwme">
                <h2 className={`text-xl font-semibold ${dk.textPrimary}`} data-id="element-13406" data-mp="3jqlnha7zzpge">
                  {o.title}
                </h2>
                <div className="flex items-center mt-2 md:mt-0 gap-4" data-id="element-13407" data-mp="3esk38lyu5k3c">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${rt.badge}`} data-id="element-13408" data-mp="fakfj8328k4p">
                    {o.status}
                  </span>
                  <span className={`text-sm ${dk.textMuted}`} data-id="element-13409" data-mp="69cjkwo0nvds">
                    Posted {new Date(o.postedDate).toLocaleDateString()}
                  </span>
                </div>
              </div>
              <p className={`mb-4 ${dk.textSecondary}`} data-id="element-13410" data-mp="2r2prk7q961ko">{o.description}</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-13411" data-mp="2m10gis315nwl">
                <div className={`flex items-center gap-2 text-sm ${dk.textSecondary}`} data-id="element-13412" data-mp="1r3urxfyab576">
                  <ClockIcon className={`h-4 w-4 ${dk.textFaint}`} data-id="element-13413" data-mp="1m62t01z82ith" />
                  Timeline: {o.timeline}
                </div>
                <div className={`flex items-center gap-2 text-sm ${dk.textSecondary}`} data-id="element-13414" data-mp="20v8qnliew0z9">
                  <DollarSignIcon className={`h-4 w-4 ${dk.textFaint}`} data-id="element-13415" data-mp="29um04e6jcpjz" />
                  Budget: {o.budget}
                </div>
                <div className={`flex items-center gap-2 text-sm ${dk.textSecondary}`} data-id="element-13416" data-mp="24waqo4k5kh2a">
                  <UserIcon className={`h-4 w-4 ${dk.textFaint}`} data-id="element-13417" data-mp="2jrguckvs03sg" />
                  {o.applicants} applicants
                </div>
              </div>
              <div className="mb-4" data-id="element-13418" data-mp="pehc0k00gvhh">
                <h3 className={`text-sm font-medium mb-2 ${dk.textPrimary}`} data-id="element-13419" data-mp="pfk1cnhoqpva">
                  Requirements:
                </h3>
                <ul className="list-disc list-inside space-y-1" data-id="element-13420" data-mp="khqxi13uu7y6">
                  {o.requirements.map((req, i) => <li key={i} className={`text-sm ${dk.textSecondary}`} data-id="element-13421" data-mp="3124c7be7ocms" data-mpi={i}>
                      {req}
                    </li>)}
                </ul>
              </div>
              <div className="flex flex-wrap gap-2 mb-4" data-id="element-13422" data-mp="2mbdl65kg1hkq">
                {o.format.map(f => <span key={f} className={`px-2 py-0.5 ${rt.badgeRadius} text-xs font-medium ${dk.badgeNeutral}`} data-id="element-13423" data-mp="2r9y9p7qc2ctc">
                    {f}
                  </span>)}
              </div>
              <div className="flex justify-between items-center" data-id="element-13424" data-mp="23fnggal4m735">
                <div className="text-sm text-red-500" data-id="element-13425" data-mp="28cx2rhd7y2mr">
                  Deadline: {new Date(o.deadline).toLocaleDateString()}
                </div>
                <button className={`px-4 py-2 rounded-md text-sm font-medium text-white ${rt.primaryButton}`} data-id="element-13426" data-mp="1ns8o61zqmq7">
                  Apply Now
                </button>
              </div>
            </div>)}
          {filtered.length === 0 && <div className={`text-center py-12 rounded-xl border-2 border-dashed ${dk.emptyState}`} data-id="element-13427" data-mp="48qduxek7ow">
              <p className={`text-lg ${dk.textMuted}`} data-id="element-13428" data-mp="3rdy0tj9huhx6">
                No opportunities found matching your criteria.
              </p>
            </div>}
        </div>
      </div>
    </div>;
};
export default ProductionOpportunities;